import { useState } from 'react';
import { AffilmasterApiError, postAffilmasterSocialsTest, type SocialProbeResult } from '../api/affilmaster';
import { useFeather } from '../shell/useFeather';
import './affilmaster-socials.css';

const DEFAULT_URLS = [
  'https://adamkovaherna.cz',
  'https://www.youtube.com/@AdamkovaHerna',
  'https://www.tiktok.com/@vyhraj',
  'https://www.instagram.com/adamkova_herna/',
  'https://www.facebook.com/groups/stiracilosy/',
  'https://www.twitch.tv/adamko',
];

const PLATFORM_LABEL: Record<SocialProbeResult['platform'], string> = {
  youtube: 'YouTube',
  tiktok: 'TikTok',
  instagram: 'Instagram',
  facebook: 'Facebook',
  twitch: 'Twitch',
  kick: 'Kick',
  telegram: 'Telegram',
  twitter: 'Twitter',
  web: 'Web',
};

const PLATFORM_ICON: Record<SocialProbeResult['platform'], string> = {
  youtube: 'youtube',
  tiktok: 'music',
  instagram: 'instagram',
  facebook: 'facebook',
  twitch: 'twitch',
  kick: 'video',
  telegram: 'send',
  twitter: 'twitter',
  web: 'globe',
};

const STATUS_LABEL: Record<SocialProbeResult['status'], string> = {
  ok: 'Načteno',
  error: 'Chyba',
  unconfigured: 'Není nastaveno',
  unsupported: 'Nepodporováno',
};

const nf = new Intl.NumberFormat('cs-CZ');

function fmtMetric(v: SocialProbeResult['metrics'][number]['value']): string {
  if (v == null) return '—';
  if (typeof v === 'boolean') return v ? 'Ano' : 'Ne';
  if (typeof v === 'number') return nf.format(v);
  if (/^\d{4}-\d{2}-\d{2}T/.test(v)) return fmtDateTime(v);
  return v;
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function fmtCount(v: number | null): string {
  return v == null ? '—' : nf.format(v);
}

function fmtDuration(ms: number): string {
  return ms >= 1000 ? `${(ms / 1000).toLocaleString('cs-CZ', { maximumFractionDigits: 1 })} s` : `${ms} ms`;
}

function profileEntries(profile: Record<string, unknown> | null): [string, string][] {
  if (!profile) return [];
  return Object.entries(profile)
    .filter(([k, v]) => k !== 'note' && v != null && v !== '' && typeof v !== 'object')
    .map(([k, v]) => [k, typeof v === 'boolean' ? (v ? 'Ano' : 'Ne') : String(v)]);
}

export function AffilmasterSocialsTestPage() {
  const [text, setText] = useState(DEFAULT_URLS.join('\n'));
  const [results, setResults] = useState<SocialProbeResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useFeather([results, loading, err]);

  const urls = text.split('\n').map((s) => s.trim()).filter(Boolean);

  const run = async () => {
    if (urls.length === 0 || loading) return;
    setLoading(true);
    setErr(null);
    try {
      setResults(await postAffilmasterSocialsTest(urls));
    } catch (e: unknown) {
      setErr(e instanceof AffilmasterApiError ? e.message : 'Načtení sondy selhalo');
    } finally {
      setLoading(false);
    }
  };

  const okCount = results?.filter((r) => r.status === 'ok').length ?? 0;

  return (
    <section className="am-socials">
      <header className="prototype-page-head am-socials-page-head">
        <div>
          <div className="prototype-eyebrow">AFFILMASTER · SOCIALS</div>
          <h1>Test sběru dat</h1>
          <p>Živá sonda sociálních profilů a webů podle URL. Služba na pozadí pro CRM a karty partnerů.</p>
        </div>
        {results && (
          <div className="am-socials-head-meta">
            <span><i data-feather="activity" />{okCount} / {results.length} zdrojů</span>
            <span>Načteno {fmtDateTime(results[0]?.fetchedAt ?? null)}</span>
          </div>
        )}
      </header>

      <article className="prototype-panel am-socials-input">
        <label htmlFor="am-socials-urls">Adresy profilů a webů (jedna na řádek, max 10)</label>
        <textarea id="am-socials-urls" value={text} onChange={(e) => setText(e.target.value)} rows={7} spellCheck={false} disabled={loading} />
        <div className="am-socials-input-actions">
          <span>{urls.length} {urls.length === 1 ? 'adresa' : urls.length >= 2 && urls.length <= 4 ? 'adresy' : 'adres'}</span>
          <button type="button" className="btn btn-primary prototype-primary-btn" onClick={run} disabled={loading || urls.length === 0}>
            {loading ? 'Načítám…' : 'Načíst data'}
          </button>
        </div>
      </article>

      {err && (
        <div className="prototype-notice prototype-notice-error" role="alert">
          <i data-feather="alert-circle" />
          <div><strong>Sonda selhala</strong><span>{err}</span></div>
        </div>
      )}
      {loading && <div className="prototype-page-loading" aria-label="Načítám"><span /><span /><span /><span /><span /></div>}

      {results && !loading && (
        <div className="am-socials-results">
          {results.map((r, i) => <ResultCard key={`${r.inputUrl}-${i}`} r={r} />)}
        </div>
      )}
    </section>
  );
}

function ResultCard({ r }: { r: SocialProbeResult }) {
  const [showProfile, setShowProfile] = useState(false);
  const entries = profileEntries(r.profile);
  return (
    <article className={`prototype-panel am-socials-card is-${r.status}`}>
      <header className="am-socials-card-head">
        <span className={`am-socials-platform am-socials-platform-${r.platform}`}>
          {typeof r.profile?.faviconUrl === 'string' ? <img src={r.profile.faviconUrl} alt="" /> : <i data-feather={PLATFORM_ICON[r.platform]} />}
        </span>
        <div className="am-socials-card-title">
          <strong>{PLATFORM_LABEL[r.platform]}{r.handle ? ` · ${r.handle}` : ''}</strong>
          <a href={r.inputUrl} target="_blank" rel="noreferrer">{r.inputUrl}</a>
        </div>
        <div className="am-socials-card-status">
          <span className={`am-socials-badge is-${r.status}`}>{STATUS_LABEL[r.status]}</span>
          <small>{r.source} · {fmtDuration(r.durationMs)}</small>
        </div>
      </header>

      {r.error && <p className="am-socials-error"><i data-feather="alert-triangle" />{r.error}</p>}
      {typeof r.profile?.note === 'string' && <p className="am-socials-note"><i data-feather="info" />{r.profile.note}</p>}

      {r.metrics.length > 0 && (
        <dl className="am-socials-metrics">
          {r.metrics.map((m) => (
            <div key={m.key}><dt>{m.label}</dt><dd>{fmtMetric(m.value)}</dd></div>
          ))}
        </dl>
      )}

      {r.keywords && r.keywords.length > 0 && (
        <div className="am-socials-keywords">
          <div className="am-socials-items-head"><span>Pozice na klíčová slova ({r.keywords[0]?.country.toUpperCase()})</span></div>
          <ul>
            {r.keywords.map((k) => (
              <li key={k.keyword}>
                <span>{k.keyword}</span>
                <strong className={k.position == null ? 'is-none' : ''}>{k.position != null ? `${k.position}.` : `mimo top ${k.checkedTop}`}</strong>
              </li>
            ))}
          </ul>
        </div>
      )}

      {r.items.length > 0 && (
        <div className="am-socials-items">
          <div className="am-socials-items-head"><span>Poslední obsah</span><small>{r.items.length} položek</small></div>
          <table>
            <thead>
              <tr><th>Titulek</th><th>Datum</th><th className="num">Zhlédnutí</th><th className="num">Lajky</th><th className="num">Komentáře</th></tr>
            </thead>
            <tbody>
              {r.items.map((it, i) => (
                <tr key={it.externalId ?? i}>
                  <td className="am-socials-item-title">{it.url ? <a href={it.url} target="_blank" rel="noreferrer">{it.title || '(bez titulku)'}</a> : (it.title || '(bez titulku)')}</td>
                  <td>{fmtDateTime(it.publishedAt)}</td>
                  <td className="num">{fmtCount(it.views)}</td>
                  <td className="num">{fmtCount(it.likes)}</td>
                  <td className="num">{fmtCount(it.comments)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {entries.length > 0 && (
        <div className="am-socials-profile">
          <button type="button" className="am-socials-toggle" onClick={() => setShowProfile((v) => !v)}>
            <i data-feather={showProfile ? 'chevron-up' : 'chevron-down'} />{showProfile ? 'Skrýt detail profilu' : `Detail profilu (${entries.length} polí)`}
          </button>
          {showProfile && (
            <dl className="am-socials-profile-grid">
              {entries.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
            </dl>
          )}
        </div>
      )}
    </article>
  );
}
